import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, ChevronLeft, ChevronRight, Heart, MessageCircle, Sparkles } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Tappable } from "@/context/LightboxContext";
import VerifiedBadge from "@/components/app/VerifiedBadge";

// Horizontal "Stories"-style strip rendered at the top of Discover. Pulls the
// public Moments feed (visibility filtering already applied server-side),
// dedupes by author so each face appears once, opens a fullscreen swipeable
// viewer on tap.
export default function StoriesStrip() {
  const [stories, setStories] = useState([]);
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    api
      .get("/moments/feed", { params: { limit: 30 } })
      .then((r) => {
        const seen = new Set();
        const grouped = [];
        for (const m of r.data || []) {
          if (seen.has(m.user_id)) continue;
          seen.add(m.user_id);
          grouped.push(m);
        }
        setStories(grouped.slice(0, 12));
      })
      .catch(() => setStories([]));
  }, []);

  if (stories.length === 0) {
    return (
      <div
        data-testid="stories-empty-strip"
        className="flex gap-3 overflow-x-auto px-4 pt-3 pb-2 scrollbar-hide lg:pt-2 lg:pb-1"
      >
        <Link
          to="/moments/new"
          data-testid="stories-empty-cta"
          className="flex flex-col items-center gap-1 shrink-0 w-[68px] lg:w-[62px]"
        >
          <div className="w-[58px] h-[58px] rounded-full bg-[#B95F73]/15 border-2 border-dashed border-[#B95F73]/70 flex items-center justify-center lg:w-[52px] lg:h-[52px]">
            <Plus className="w-5 h-5 text-[#B95F73]" />
          </div>
          <span className="text-[10px] tracking-wide text-[#C8C2B5] truncate max-w-full font-semibold">Be the first ✨</span>
        </Link>
        {["Video bios", "Date fits", "Your vibe"].map((label, i) => (
          <Link
            key={label}
            to="/moments/new"
            data-testid={`stories-teaser-${i}`}
            className="flex flex-col items-center gap-1 shrink-0 w-[68px] lg:w-[62px]"
          >
            <div className="p-[2px] rounded-full bg-gradient-to-tr from-[#B95F73]/40 via-[#C9A961]/40 to-[#8FE0C5]/40">
              <div className="rounded-full p-[2px] bg-[#16171A]">
                <div className="w-[50px] h-[50px] rounded-full bg-[#1B1C1F] flex items-center justify-center lg:w-[44px] lg:h-[44px]">
                  <Sparkles className="w-4 h-4 text-[#C9A961]/70" />
                </div>
              </div>
            </div>
            <span className="text-[10px] tracking-wide text-[#6B6B72] truncate max-w-full">{label}</span>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <>
      <StoryStripRow stories={stories} setActiveIdx={setActiveIdx} />

      <AnimatePresence>
        {activeIdx !== null && (
          <StoryViewer
            stories={stories}
            startIdx={activeIdx}
            onClose={() => setActiveIdx(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function StoryViewer({ stories, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const videoRef = useRef(null);
  const story = stories[idx];

  const next = () => (idx < stories.length - 1 ? setIdx(idx + 1) : onClose());
  const prev = () => (idx > 0 ? setIdx(idx - 1) : null);

  // View ping
  useEffect(() => {
    api.post(`/moments/${story?.id}/view`).catch(() => {});
  }, [story?.id]);

  // Reliable video playback + auto-advance.
  //   • Re-load + .play() each time idx changes (Android Chrome won't autoplay
  //     a freshly swapped <video src> attribute without an explicit nudge).
  //   • Mute by default so the browser's autoplay policy doesn't block it.
  //   • Fallback safety timer: if `onEnded` never fires (mobile browsers can
  //     swallow it when the video stalls or buffers), advance after the
  //     known duration + 1s, or after a hard 12s cap as last resort.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    try { v.load(); } catch (_) { /* ignore */ }
    const playPromise = v.play();
    if (playPromise && playPromise.catch) playPromise.catch(() => {});

    const cap = Math.min(12, (story?.duration || 8) + 1) * 1000;
    const safety = setTimeout(() => next(), cap);
    return () => clearTimeout(safety);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, story?.id]);

  if (!story) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
      data-testid="story-viewer"
    >
      <video
        ref={videoRef}
        src={fileUrl(story.storage_path)}
        className="w-full h-full object-contain bg-black"
        autoPlay
        muted
        playsInline
        controls={false}
        onEnded={next}
        onError={next}
      />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 pt-[max(env(safe-area-inset-top),16px)] px-4 pb-3 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex gap-1 mb-3">
          {stories.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
              <div
                className={`h-full bg-white ${i < idx ? "w-full" : i === idx ? "w-full animate-pulse" : "w-0"}`}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {story.user?.photos?.[0] && (
              <Tappable
                src={fileUrl(story.user.photos[0].storage_path, 160)}
                photos={(story.user.photos || []).map((p) => fileUrl(p.storage_path))}
                alt=""
                className="w-8 h-8 rounded-full object-cover"
                testid="story-viewer-avatar"
              />
            )}
            <span className="text-white text-sm font-display truncate">
              {story.user?.name}
            </span>
            <VerifiedBadge user={story.user} size={10} />
          </div>
          <button
            onClick={onClose}
            data-testid="story-close"
            className="w-9 h-9 rounded-full bg-black/40 flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Tap zones — constrained to the middle vertical band so they
          never overlap the top/bottom action bars. */}
      <button onClick={prev} className="absolute left-0 top-24 bottom-24 w-1/3 z-0" aria-label="Previous" />
      <button onClick={next} className="absolute right-0 top-24 bottom-24 w-1/3 z-0" aria-label="Next" />

      {/* Bottom bar — caption + actions */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pb-[max(env(safe-area-inset-bottom),16px)] px-4 pt-6 bg-gradient-to-t from-black/80 to-transparent">
        {story.caption && (
          <p className="text-white text-sm mb-3 max-w-[80%]">{story.caption}</p>
        )}
        <div className="flex items-center gap-3">
          <Link
            to={`/moments?focus=${story.id}`}
            className="flex items-center gap-1.5 text-white/90 text-xs font-bold tracking-widest uppercase"
            onClick={onClose}
          >
            <Heart className="w-4 h-4" /> React
          </Link>
          <Link
            to={`/moments?focus=${story.id}`}
            className="flex items-center gap-1.5 text-white/90 text-xs font-bold tracking-widest uppercase"
            onClick={onClose}
          >
            <MessageCircle className="w-4 h-4" /> Comment
          </Link>
        </div>
      </div>
    </motion.div>
  );
}


/**
 * StoryStripRow — the horizontally-scrolling row of story circles.
 * On lg+ shows left/right chevron buttons that scroll the strip programmatically
 * (laptop users on a touchpad shouldn't have to figure out horizontal swipe).
 * The buttons only appear when there's actually overflow in that direction.
 */
function StoryStripRow({ stories, setActiveIdx }) {
  const scrollerRef = useRef(null);
  const [canScrollL, setCanScrollL] = useState(false);
  const [canScrollR, setCanScrollR] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollL(el.scrollLeft > 8);
    setCanScrollR(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [stories.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(220, el.clientWidth * 0.6), behavior: "smooth" });
  };

  return (
    <div className="relative">
      {/* Left arrow — desktop only, hidden when at start */}
      <button
        type="button"
        onClick={() => scrollBy(-1)}
        data-testid="stories-scroll-left"
        aria-label="Scroll stories left"
        className={`hidden lg:flex absolute left-1 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#16171A]/90 backdrop-blur border border-white/[0.08] items-center justify-center text-[#F0E6D2] hover:bg-[#1F2024] transition-opacity ${
          canScrollL ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={2.2} />
      </button>

      <div
        ref={scrollerRef}
        data-testid="stories-strip"
        className="flex gap-3 overflow-x-auto px-4 pt-3 pb-2 scrollbar-hide lg:pt-2 lg:pb-1"
      >
        <Link
          to="/moments/new"
          data-testid="stories-add"
          className="flex flex-col items-center gap-1 shrink-0 w-[68px] lg:w-[62px]"
        >
          <div className="w-[58px] h-[58px] rounded-full bg-[#1B1C1F] border-2 border-dashed border-[#B95F73]/60 flex items-center justify-center lg:w-[52px] lg:h-[52px]">
            <Plus className="w-5 h-5 text-[#B95F73]" />
          </div>
          <span className="text-[10px] tracking-wide text-[#A0A0A8] truncate max-w-full">Your moment</span>
        </Link>
        {stories.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setActiveIdx(i)}
            data-testid={`story-thumb-${s.user_id}`}
            className="flex flex-col items-center gap-1 shrink-0 w-[68px] lg:w-[62px]"
          >
            <div className="p-[2px] rounded-full bg-gradient-to-tr from-[#B95F73] via-[#C9A961] to-[#8FE0C5]">
              <div className="rounded-full p-[2px] bg-[#16171A]">
                {s.user?.photos?.[0] ? (
                  <img
                    src={fileUrl(s.user.photos[0].storage_path, 160)}
                    alt={s.user?.name}
                    loading="lazy"
                    decoding="async"
                    className="w-[54px] h-[54px] rounded-full object-cover lg:w-[48px] lg:h-[48px]"
                  />
                ) : (
                  <div className="w-[54px] h-[54px] rounded-full bg-[#25252A] flex items-center justify-center text-[#F0E6D2] font-display lg:w-[48px] lg:h-[48px]">
                    {s.user?.name?.[0] || "?"}
                  </div>
                )}
              </div>
            </div>
            <span className="text-[10px] tracking-wide text-[#C8C2B5] truncate max-w-full">
              {s.user?.name || "—"}
            </span>
          </button>
        ))}
      </div>

      {/* Right arrow — desktop only, hidden when at end */}
      <button
        type="button"
        onClick={() => scrollBy(1)}
        data-testid="stories-scroll-right"
        aria-label="Scroll stories right"
        className={`hidden lg:flex absolute right-1 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#16171A]/90 backdrop-blur border border-white/[0.08] items-center justify-center text-[#F0E6D2] hover:bg-[#1F2024] transition-opacity ${
          canScrollR ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <ChevronRight className="w-4 h-4" strokeWidth={2.2} />
      </button>
    </div>
  );
}
